import React, { useState } from 'react';
import { formatRelativeTime } from './myBriefsUtils';

/**
 * Collapsible "Published to CMS" panel.
 *
 * Renders for any draft that has been published to one or more CMS
 * integrations (currently IPPEN, designed so additional integrations show up
 * automatically as new entries). Each entry surfaces the integration name
 * and the external ID returned by the CMS, with a link to the CMS preview
 * when the integration provides one.
 */
function StatusBadge({ status }) {
  const cls = `my-briefs-cms-status my-briefs-cms-status-${status}`;
  const label = status === 'published'
    ? 'Published'
    : status === 'draft'
      ? 'Draft'
      : status?.[0]?.toUpperCase() + status?.slice(1) || 'Unknown';
  return <span className={cls}>{label}</span>;
}

function PublicationRow({ publication, nowMs }) {
  const {
    clientName,
    externalId,
    externalUrl,
    publishedAt,
    status,
    language,
  } = publication || {};

  const externalLabel = externalId ? `#${externalId}` : null;

  return (
    <li className="my-briefs-cms-publication-row" data-testid={`my-briefs-cms-publication-${externalId || 'unknown'}`}>
      <div className="my-briefs-cms-publication-main">
        <span className="my-briefs-cms-publication-client" title={clientName || 'CMS integration'}>
          {clientName || 'CMS integration'}
        </span>
        {externalLabel ? (
          externalUrl ? (
            <a
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="my-briefs-cms-publication-id-link"
            >
              {externalLabel}
            </a>
          ) : (
            <span className="my-briefs-cms-publication-id">{externalLabel}</span>
          )
        ) : null}
      </div>
      <div className="my-briefs-cms-publication-meta">
        <StatusBadge status={status} />
        {language ? <span className="my-briefs-cms-publication-lang">{language}</span> : null}
        {publishedAt ? <span>{formatRelativeTime(publishedAt, nowMs)}</span> : null}
      </div>
    </li>
  );
}

function MyBriefsCmsPublications({ publications, briefId, defaultOpen = false, nowMs = Date.now() }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!Array.isArray(publications) || publications.length === 0) return null;

  const count = publications.length;

  return (
    <section
      className={`my-briefs-cms-publications${open ? ' is-open' : ''}`}
      data-testid={`my-briefs-cms-publications-${briefId}`}
    >
      <button
        type="button"
        className="my-briefs-cms-publications-toggle"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-controls={`my-briefs-cms-publications-list-${briefId}`}
      >
        <span
          className={`my-briefs-cms-publications-chevron${open ? ' my-briefs-cms-publications-chevron-open' : ''}`}
          aria-hidden="true"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </span>
        <span className="my-briefs-cms-publications-title">
          Published to CMS ({count})
        </span>
      </button>
      {open ? (
        <ul
          id={`my-briefs-cms-publications-list-${briefId}`}
          className="my-briefs-cms-publications-list"
        >
          {publications.map((pub, idx) => (
            <PublicationRow
              key={pub?._id || `${pub?.externalId || 'pub'}-${idx}`}
              publication={pub}
              nowMs={nowMs}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

export default MyBriefsCmsPublications;
